import { ChangeEvent, FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb, BreadcrumbTemplateEnum, Breadcrumbs, LoadingSpinner, Page, PageHeader, PageHeaderRight, PageInner, PageSection, TooltipWrapper } from "@app/shared";
import { AdminUsersContext, AdminUsersProvider } from "../contexts/AdminUsersContext";
import { QESAlert, QESButton, WizardNavigationBar } from "@app/shared/qes-standards";
import * as userViews from "./views";
import { GeographyService, BasicState } from "../../geography";
import { Link, Redirect, RouteComponentProps } from "react-router-dom";
import { RoleRestricted, Roles, UserContext } from "../../user";
import { toast } from "react-toastify";
import { UserStatus } from "./userModel";
import { ModalLink } from "@app/shared/Modals";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface UserMatchProps {
  userId?: string;
}

interface UserDetailsRouteState {
  clientId?: number;
}

type UserProps = RouteComponentProps<UserMatchProps, undefined, UserDetailsRouteState>;
type UserViews = "Profile" | "Roles" | "Guest Logins";

const UserDetails: FC<React.PropsWithChildren<UserProps>> = (props: UserProps) => {
  // this is the user operating the application, not the user being administered
  const user = useContext(UserContext);
  const adminUsersContext = useContext(AdminUsersContext);
  const [activeView, setActiveView] = useState<UserViews>("Profile");
  const [states, setStates] = useState<BasicState[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [redirect, setRedirect] = useState("");
  // to be clear, this is the user that's being created or edited
  const userExists = useRef(!!props.match.params.userId).current;
  // and this is the user that's operating the app
  const userIsQuestAdmin = user.roles.includes(Roles.QUEST_ADMIN);
  const deactivateUserTooltip = "User will be marked as \"Deactivated\" and may not log in.";
  const activateUserTooltip = "User will be marked as \"Activated\". Email confirmation will be skipped.";
  const deleteUserTooltip = "User will be disabled and marked as \"Deleted.\"";
  const unDeleteUserTooltip = "User will be returned to their previous status (Activated, Deactivated, Unconfirmed, etc.).";

  useEffect(() => {
    if (props.match.params.userId) {
      adminUsersContext.setUserId(parseInt(props.match.params.userId, 10));
    }
  }, [props.match.params.userId]);

  useEffect(() => {
    const geoService = new GeographyService();
    geoService.getStatesForDroplist().then((states) => setStates(states));
  }, []);

  useEffect(() => {
    if (userIsQuestAdmin) { // user is quest admin so set the client id to the "current" client id if there is one
      if (!userExists && props.location.state?.clientId) {
        adminUsersContext.setClientId(props.location.state.clientId);
      }
    } else { // user is not quest admin, so can only work in the context of their own client
      adminUsersContext.setClientId(user.clientId);
    }
  }, []);

  const handleCreate = () => {
    setSubmitting(true);
    adminUsersContext.createUser().then(() => {
      toast(<span>User {adminUsersContext.email} has been created.</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            handleRedirect();
          },
          type: "success",
        },
      );
    }, (error) => {
      toast(<span>We encountered a problem creating a new user. {error.responseJSON.detail}</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  }

  const resendWelcomeEmail = () => {
    adminUsersContext.resendWelcomeEmail().then(() => {
      toast(<span>Welcome email was sent to {adminUsersContext.email}.</span>,
        {
          containerId: "z-index-9999",
          type: "success",
        },
      );
    }, (error) => {
      toast(<span>Unable to send welcome email to this user. {error.responseJSON.detail}</span>,
        {
          containerId: "z-index-9999",
          type: "error",
        },
      );
    });
  }

  const handleUpdate = () => {
    setSubmitting(true);
    adminUsersContext.updateUser().then(() => {
      toast(<span>User {adminUsersContext.userName} has been updated.</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            handleRedirect();
          },
          type: "success",
        },
      );
    }, (error) => {
      toast(<span>We encountered a problem updating this user. {error.responseJSON.detail}</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  }

  const handleActivate = (isActive: boolean) => {
    setSubmitting(true);
    const activatedText = isActive ? "activated" : "deactivated";
    adminUsersContext.activateUser(isActive).then(() => {
      toast(<span>User {adminUsersContext.userName} has been {activatedText}.</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            handleRedirect();
          },
          type: "success",
        },
      );
    }, (error) => {
      toast(<span>We encountered a problem activating this user. {error.responseJSON.detail}</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  }

  const handleDelete = (isDeleted: boolean) => {
    setSubmitting(true);
    const deletedText = isDeleted ? "deleted" : "un-deleted";
    adminUsersContext.deleteUser(isDeleted).then(() => {
      toast(<span>User {adminUsersContext.userName} has been {deletedText}.</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            handleRedirect();
          },
          type: "success",
        },
      );
    }, (error) => {
      toast(<span>We encountered a problem deleting this user. {error.responseJSON.detail}</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  }

  const handleCreateGuestLogins = () => {
    setSubmitting(true);
    adminUsersContext.createGuestLogins().then(() => {
      toast(<span>Guest Logins for User {adminUsersContext.userName} have been created.</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            handleRedirect();
          },
          type: "success",
        },
      );
    }, (error) => {
      toast(<span>We encountered a problem creating guest logins for this user. {error.responseJSON.detail}</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  }

  const handleCancel = (): void => {
    handleRedirect();
  }

  const handleRedirect = (): void => {
    if (props.location.state?.clientId !== undefined) {
      setRedirect(`/admin/client-administration/${props.location.state.clientId}`);
    } else {
      props.history.goBack();
      setRedirect(props.location.pathname);
    }
  }

  const handleClientChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const clientId = parseInt(e.target.value, 10);
    adminUsersContext.setClientId(clientId);
    adminUsersContext.getClientNameByClientId(clientId);
    adminUsersContext.setRoles([]);
    adminUsersContext.checkUserNameAvailability(userExists ? adminUsersContext.userName : adminUsersContext.email);
  };

  const breadcrumbs: (string | Breadcrumb)[] = [
    userExists ? adminUsersContext.userName || "Loading..." : "Create User",
  ];

  const noClient = !adminUsersContext.clientId || adminUsersContext.clientId <= 0;
  const deletedUser = adminUsersContext.status === UserStatus.DELETED;

  const invalidFields = useMemo(() => {
    const keys = Object.entries(adminUsersContext.validationObject).filter((entry) => !entry[1]).map((entry) => entry[0]);
    return keys.map((key) => <li key={key}>{adminUsersContext.fieldNameToVisibleName[key]}</li>);
  }, [adminUsersContext.validationObject]);



  /* eslint-disable sort-keys */
  // Unfortunately can't use UserViews as the key type here
  const userDetailViews: { [key: string]: JSX.Element } = useMemo(() => {
    const views = {
      "Profile": (
        <div className="col-8">
          <PageSection>
            <userViews.UserInfo
              clientId={adminUsersContext.clientId}
              states={states}
              userExists={userExists}
              userIsQuestAdmin={userIsQuestAdmin}
              handleClientChange={handleClientChange}
              resendWelcomeEmail={resendWelcomeEmail}
            />
          </PageSection>
        </div>
      ),
      "Roles": (
        <div className="col-12">
          <PageSection>
            <userViews.UserRoles
              disabled={noClient || deletedUser}
            />
          </PageSection>
        </div>
      ),
      "Guest Logins": (
        <div className="col-12">
          <userViews.GuestLogins
            disabled={noClient || deletedUser}
          />
        </div >
      ),
    };

    return views;
  }, [noClient, deletedUser, states, adminUsersContext.clientId, userExists]);
  /* eslint-enable sort-keys */

  const loading = adminUsersContext.loadingUser || (adminUsersContext.clientId !== -1 && (adminUsersContext.allowedRoles.length === 0 || adminUsersContext.allowedDomains.length === 0));

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <Page documentTitle={`${userExists ? "User Details" : "Create User"} - Admin`}>
      <Breadcrumbs template={BreadcrumbTemplateEnum.UserAdministration} items={breadcrumbs} />
      <PageInner>
        <PageHeader title={userExists ? "User Details" : "Create User"} iconName={QesIcons.FontAwesome.User} disableHR={false}>
          {userExists && !adminUsersContext.loadingUser && !submitting && (
            <PageHeaderRight>
              {adminUsersContext.clientId === 1 &&
                (
                  <TooltipWrapper position="bottom" text='Guest Logins for this user will be created across all clients.'>
                    <QESButton qesType="secondary" qesTestId="update-user__create_guest_logins" onClick={() => handleCreateGuestLogins()} disabled={submitting || adminUsersContext.status !== UserStatus.ACTIVATED || adminUsersContext.changedFields().length > 0 || (adminUsersContext.changedFields().length === 0 && !(adminUsersContext.questViewerGuestLoginsAllowed || adminUsersContext.resourceAdminGuestLoginsAllowed))}>
                      Create Guest Logins
                    </QESButton>
                  </TooltipWrapper>
                )}
              {adminUsersContext.status === UserStatus.ACTIVATED ?
                (
                  adminUsersContext.client?.restrictedUserCreationEnabled ?
                    (
                      <TooltipWrapper position="bottom" text={deactivateUserTooltip}>
                        <ModalLink
                          classNames="mr-3 qes-button-secondary"
                          disabled={!adminUsersContext.formIsValid || submitting || noClient}
                          content={{
                            body: <>This client requires proper authorization to deactivate a user.<br />Are you sure you want to continue?</>,
                            cancelButtonText: "Cancel",
                            confirmButtonAction: () => { handleActivate(false) },
                            confirmButtonText: "Deactivate",
                            confirmButtonType: "primary-danger",
                            title: "Deactivate User - Warning",
                          }}
                          testId="user-details__deactivate-user-modal"
                        >
                          Deactivate User
                        </ModalLink>
                      </TooltipWrapper>
                    ) : (
                      <TooltipWrapper position="bottom" text={deactivateUserTooltip}>
                        <QESButton qesType="secondary" qesTestId="update-user__deactivate_user" onClick={() => handleActivate(false)}>Deactivate User</QESButton>
                      </TooltipWrapper>
                    )
                ) : adminUsersContext.status !== UserStatus.DELETED &&
                (
                  adminUsersContext.client?.restrictedUserCreationEnabled ?
                    (
                      <TooltipWrapper position="bottom" text={activateUserTooltip}>
                        <ModalLink
                          classNames="mr-3 qes-button-secondary"
                          disabled={!adminUsersContext.formIsValid || submitting || noClient}
                          content={{
                            body: <>This client requires proper authorization to activate a user.<br />Are you sure you want to continue?</>,
                            cancelButtonText: "Cancel",
                            confirmButtonAction: () => { handleActivate(true) },
                            confirmButtonText: "Activate",
                            title: "Activate User - Warning",
                          }}
                          testId="user-details__activate-user-modal"
                        >
                          Activate User
                        </ModalLink>
                      </TooltipWrapper>
                    ) : (
                      <TooltipWrapper position="bottom" text={activateUserTooltip}>
                        <QESButton qesType="secondary" qesTestId="update-user__activate_user" onClick={() => handleActivate(true)}>Activate User</QESButton>
                      </TooltipWrapper>
                    )
                )}
              {adminUsersContext.status !== UserStatus.DELETED &&
                (
                  adminUsersContext.client?.restrictedUserCreationEnabled ?
                    <TooltipWrapper position="bottom" text={deleteUserTooltip}>
                      <ModalLink
                        classNames="mr-3 qes-button-primary"
                        disabled={!adminUsersContext.formIsValid || submitting || noClient}
                        content={{
                          body: <>This client requires proper authorization to delete a user.<br />Are you sure you want to continue?</>,
                          cancelButtonText: "Cancel",
                          confirmButtonAction: () => { handleDelete(true) },
                          confirmButtonText: "Delete",
                          confirmButtonType: "primary-danger",
                          title: "Delete User - Warning",
                        }}
                        testId="user-details__delete-user-modal"
                      >
                        Delete User
                      </ModalLink>
                    </TooltipWrapper>
                    :
                    <TooltipWrapper position="bottom" text={deleteUserTooltip}>
                      <QESButton qesType="secondary" qesTestId="update-user__delete_user" onClick={() => handleDelete(true)}>Delete User</QESButton>
                    </TooltipWrapper>
                )
              }
              {adminUsersContext.status === UserStatus.DELETED &&
                (
                  adminUsersContext.client?.restrictedUserCreationEnabled ?
                    (
                      <TooltipWrapper position="bottom" text={deleteUserTooltip}>
                        <ModalLink
                          classNames="mr-3 qes-button-primary"
                          disabled={!adminUsersContext.formIsValid || submitting || noClient}
                          content={{
                            body: <>This client requires proper authorization to un-delete a user.<br />Are you sure you want to continue?</>,
                            cancelButtonText: "Cancel",
                            confirmButtonAction: () => { handleDelete(false) },
                            confirmButtonText: "Delete",
                            title: "Un-Delete User - Warning",
                          }}
                          testId="user-details__undelete-user-modal"
                        >
                          Un-Delete User
                        </ModalLink>
                      </TooltipWrapper>
                    ) :
                    <TooltipWrapper position="bottom" text={unDeleteUserTooltip}>
                      <QESButton qesType="secondary" qesTestId="update-user__undelete_user" onClick={() => handleDelete(false)}>Un-Delete User</QESButton>
                    </TooltipWrapper>
                )}
              <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN]}>
                <Link
                  className="qes-button qes-button-secondary"
                  data-testid="client-details__role-assignments"
                  to={{
                    pathname: "/admin/internal-reports/role-assignments",
                    search: `?user=${encodeURIComponent(adminUsersContext.userName)}`
                  }}
                >
                  Role Assignments
                </Link>
              </RoleRestricted>
            </PageHeaderRight>
          )}
        </PageHeader>
        <PageSection>
          {loading && <div className="mt-3"><LoadingSpinner /></div>}
          {!loading && !submitting && (
            <>
              {invalidFields.length > 0 && adminUsersContext.userId ? (
                <QESAlert type="danger" collapseTitle="Invalid Fields">
                  {invalidFields}
                </QESAlert>
              ) : null}
              <div className="tabs-style-2">
                <ul className="nav nav-tabs">
                  {Object.keys(userDetailViews).map((key) => (
                    <li className="nav-item" key={key}>
                      <a className={`nav-link${activeView === key ? " active" : ""}`} role="tab" onClick={() => setActiveView(key as UserViews)}>{key}</a>
                    </li>
                  ))}
                </ul>
              </div>
              {(
                <div className="row mt-3">
                  {userDetailViews[activeView]}
                </div>
              )}
            </>
          )}
        </PageSection>
        {submitting && (
          <div className="mt-3 text-center">
            <h2>User {props.match.params.userId ? `update` : `creation`} is in progress. This may take a few minutes....</h2>
            <LoadingSpinner />
          </div>
        )}
      </PageInner>
      <WizardNavigationBar
        leftSide={!submitting && (
          <QESButton qesType="secondary" qesTestId="create-user__cancel" onClick={handleCancel}>Cancel</QESButton>
        )}
        rightSide={props.match.params.userId ? (
          <QESButton qesType="primary" disabled={!adminUsersContext.formIsValid || submitting || adminUsersContext.changedFields().length === 0} qesTestId="create-user__submit" onClick={handleUpdate}>Update</QESButton>
        ) :
          adminUsersContext.client?.restrictedUserCreationEnabled ?
            (
              <ModalLink
                classNames="mr-3 qes-button-primary"
                disabled={!adminUsersContext.formIsValid || submitting || noClient}
                content={{
                  body: <>This client requires proper authorization to create a new user.<br />Are you sure you want to continue?</>,
                  cancelButtonText: "Cancel",
                  confirmButtonAction: handleCreate,
                  confirmButtonText: "Create",
                  title: "Create User - Warning",
                }}
                testId="user-details__create-user-modal"
              >
                Create User
              </ModalLink>
            ) :
            (
              <QESButton qesType="primary" disabled={!adminUsersContext.formIsValid || submitting || noClient} qesTestId="create-user__submit" onClick={handleCreate}>Create</QESButton>
            )
        }
      />
    </Page >
  )
}

const WrappedUserDetails: FC<React.PropsWithChildren<UserProps>> = (props: UserProps) => (
  <AdminUsersProvider userId={parseInt(props.match.params.userId, 10)}>
    <UserDetails {...props} />
  </AdminUsersProvider>
);

export { WrappedUserDetails as UserDetails };
